/**
 * ZAGX v2 — Math and Calculation Functions
 * Extracted from index.html lines 1150-1240 and 2238-2370
 * Shared state reference pattern to avoid circular dependencies
 */

import { toUSD as _toUSD, toUSDAt as _toUSDAt, normTicker as _normTicker } from './utils.js';
import { netExternalByMonth, externalForMonth } from './external-capital.js';

// Shared state reference — set by state.js on init
let _S = null;

export function _setMathState(s) {
  _S = s;
}

/**
 * XIRR Calculation using Newton-Raphson + Bisection fallback
 * Pure function, no state dependency
 *
 * @param {Array<{date: Date, amount: number}>} cashflows - negative=outflow, positive=inflow
 * @returns {number|null} XIRR as decimal (e.g., 0.15 for 15%)
 */
export function xirr(cashflows) {
  // cashflows = [{date:Date, amount:number},...] — negative=outflow, positive=inflow
  if (!cashflows || cashflows.length < 2) return null;

  // ⚠ RET-005 — THIS FUNCTION READ POSITION AS IF IT WERE CHRONOLOGY, AND SAID NOTHING.
  //
  // `d0` and `lastDate` were taken from the FIRST and LAST array slots. Hand it the same flows
  // in a different order and the span it measures is not the span of the series: two flows
  // seven months apart, appended in the wrong order, produced `totalYears ≈ 0.02`, which took
  // the "<60 days" branch, annualised a 12× ratio, hit the ±500 % sanity cap and **returned
  // null**. Not a wrong number — a silently missing one.
  //
  // `xirrCashflows` sorts before calling and was safe. `calcHoldingXIRR` never sorted: its flows
  // came out of `h.lots` in ARRAY order, so **any backdated lot — exactly what `saveAddLot`
  // writes when a missed purchase is recorded late — would blank that holding's XIRR column
  // with no error anywhere.** Found 2026-08-15 because the RET-003 fix appends the un-lotted
  // remainder after the lots, which made the latent hazard fire immediately.
  //
  // Sorting a COPY here fixes every caller at once and cannot break one: the maths is a sum
  // over the series and does not depend on the order it was handed in. (rule #24)
  cashflows = cashflows.slice().sort((a, b) => a.date - b.date);

  const d0 = cashflows[0].date;
  const lastDate = cashflows[cashflows.length - 1].date;
  const totalYears = (lastDate - d0) / (365.25 * 86400000);
  // CE-05 Fix: For very short periods (<60 days), use simple annualized return
  if (totalYears < 0.164) {
    // ~60 days
    const outflows = cashflows
      .filter(cf => cf.amount < 0)
      .reduce((s, cf) => s + cf.amount, 0);
    const inflows = cashflows
      .filter(cf => cf.amount > 0)
      .reduce((s, cf) => s + cf.amount, 0);
    if (outflows === 0) return null;
    const simpleReturn = (inflows + outflows) / -outflows; // net gain / total invested
    // Annualize: (1+r)^(1/yf) - 1
    if (totalYears <= 0) return null;
    const annualized = Math.pow(1 + simpleReturn, 1 / totalYears) - 1;
    // Sanity bound: cap at ±500%
    if (annualized > 5 || annualized < -0.99) return null;
    return annualized;
  }
  const yf = d => (d - d0) / (365.25 * 86400000); // year fraction
  // NPV at rate r
  const npv = r =>
    cashflows.reduce((s, cf) => s + cf.amount / Math.pow(1 + r, yf(cf.date)), 0);
  // NPV derivative
  const dnpv = r =>
    cashflows.reduce((s, cf) => {
      const t = yf(cf.date);
      return s - (t * cf.amount) / Math.pow(1 + r, t + 1);
    }, 0);
  // Newton-Raphson
  let rate = 0.1; // initial guess 10%
  let converged = false;
  for (let i = 0; i < 200; i++) {
    const n = npv(rate);
    const d = dnpv(rate);
    if (Math.abs(d) < 1e-12) break; // derivative too small — switch to bisection
    const newRate = rate - n / d;
    if (Math.abs(newRate - rate) < 1e-9) {
      converged = true;
      rate = newRate;
      break;
    }
    rate = newRate;
    if (rate < -0.99) rate = -0.99; // floor to avoid divergence
    if (rate > 10) rate = 10; // cap at 1000%
  }
  // Bisection fallback if Newton-Raphson didn't converge
  if (!converged) {
    let lo = -0.99,
      hi = 10;
    const nLo = npv(lo),
      nHi = npv(hi);
    if (nLo * nHi > 0) return null; // no root in range
    for (let i = 0; i < 100; i++) {
      const mid = (lo + hi) / 2;
      const nMid = npv(mid);
      if (Math.abs(nMid) < 0.01) {
        rate = mid;
        converged = true;
        break;
      }
      if (nMid * npv(lo) < 0) hi = mid;
      else lo = mid;
    }
    if (!converged) rate = (lo + hi) / 2;
  }
  // Sanity bound: reject rates beyond ±500%
  if (rate > 5 || rate < -0.99) return null;
  // Verify convergence
  return Math.abs(npv(rate)) < 1 ? rate : null;
}

/**
 * Get sorted list of all investment months
 */
export function getMs() {
  return Object.keys(_S.monthlyRates).sort();
}

/**
 * TRY amount for a month (base * FX rate * allocation multiplier)
 */
export function mTRY(m) {
  const r = _S.monthlyRates[m];
  if (!r || !r.usdtry || r.usdtry <= 0) return 0;
  return _S.base * r.usdtry * (r.mul || 1);
}

/**
 * TRY amount for holding in a month
 */
export function hMoTRY(h, m) {
  // Holdings added mid-plan carry `since` (YYYY-MM). Months before it contribute no
  // plan-derived amount — without this guard a newly-added target fabricates invested
  // history across every past month (Option A rows inflated total wealth ~$8k, 2026-07-24).
  if (h.since && m < h.since) return 0;
  return mTRY(m) * (h.allocationPct / 100);
}

/**
 * Local currency amount for holding in a month
 */
export function hMoLocal(h, m) {
  const t = hMoTRY(h, m);
  const r = _S.monthlyRates[m];
  if (!r) return 0;
  if (h.currency === 'USD' && r.usdtry > 0) return t / r.usdtry;
  if (h.currency === 'EUR' && r.eurtry > 0) return t / r.eurtry;
  return t;
}

/**
 * USD investment amount for holding in a month (reconciliation-aware)
 */
export function hMoInvUSD(h, m) {
  const r = _S.monthlyRates[m];
  if (!r) return 0;

  // A RECORDED PURCHASE BEATS THE PLAN, whatever the month's status.
  //
  // The old order checked status first, so a 'reconciled' or 'legacy' month always took
  // the plan branch below — `mTRY(m) * allocationPct/100` — and never looked at
  // r.entries at all. For a holding bought with real money but carrying no allocation
  // target that returns ZERO, while the month's entry sits two lines away with its qty,
  // price and date. Live state had 29 such entries per month across four reconciled
  // months and 17 holdings reading as unfunded because of it.
  //
  // Downstream that zero is the "16 avgCost holdings missing from the cashflows" the
  // 2026-07-31 XIRR audit found, and the reason the VOO benchmark could only place
  // $9,324 of a $37,217 cost basis on a schedule. "Reconciled" means the month's totals
  // were verified — not that its per-ticker records should be discarded. (audit 2026-08-01)
  const e = r.entries && r.entries[h.ticker];
  if (e) {
    const local = (e.qty || 0) * (e.price || 0);
    if (!(local > 0)) return 0;
    // Converted at the MONTH's rate, not today's — an event-time amount needs an
    // event-time rate. The old partial/completed branch used toUSD(), i.e. today's.
    const cur = h.currency || 'USD';
    if (cur === 'TRY') return r.usdtry > 0 ? local / r.usdtry : 0;
    if (cur === 'EUR') return (r.eurtry > 0 && r.usdtry > 0) ? (local * r.eurtry) / r.usdtry : 0;
    return local;
  }

  const st = r.status || 'legacy';
  if (st === 'reconciled' || st === 'legacy' || !r.entries) {
    if (!r.usdtry || r.usdtry <= 0) return 0;
    return hMoTRY(h, m) / r.usdtry;
  }
  // Partial/completed with no entry for this holding: it was not bought that month.
  return 0;
}

/**
 * TRY investment amount for holding in a month (reconciliation-aware)
 */
export function hMoInvTRY(h, m) {
  const r = _S.monthlyRates[m];
  if (!r) return 0;
  // The TRY twin of hMoInvUSD, and it carried the identical status-first gate: a
  // 'reconciled' or 'legacy' month returned the PLAN and never reached r.entries below.
  // Consequence was visible on one screen — the monthly matrix showed a holding at ₺0
  // invested in the TRY column while the USD column beside it, which reads entries,
  // showed its real cost. The record beats the plan here too. (audit 2026-08-01)
  const e = r.entries && r.entries[h.ticker];
  if (!e) {
    const st = r.status || 'legacy';
    if (st === 'reconciled' || st === 'legacy' || !r.entries) return hMoTRY(h, m);
    return 0;
  }
  const local = (e.qty || 0) * (e.price || 0);
  if (!(local > 0)) return 0;
  if (h.currency === 'TRY') return local;
  if (h.currency === 'USD') return local * r.usdtry;
  if (h.currency === 'EUR') return local * r.eurtry;
  return local;
}

/**
 * Total TRY invested across all months for a holding
 */
export function hTotTRY(h) {
  return getMs().reduce((s, m) => s + hMoInvTRY(h, m), 0);
}

/**
 * Total USD invested across all months for a holding
 * Cash-type holdings: invested = current value (no P&L concept for cash reserves)
 * DRIP lots are excluded — they are dividend returns, not invested capital.
 */
export function hTotUSD(h) {
  if (h.type === 'Cash') {
    const v = hVal(h);
    return v.vU || 0;
  }
  // qty * avgCost FIRST, lots second — and the order matters more than it looks.
  //
  // Lots used to win outright, on the assumption that a holding with lots has ONLY lots.
  // True for the SAP plan; false the moment a lot is added to a position whose basis came in
  // as an average. ASELS carries TRY 41,736 of averaged basis and no lots; recording its
  // missing 2026-05-22 purchase as a lot would have made the cost basis TRY 79,100 — the one
  // new lot — silently deleting the other TRY 41,736. That is not a hypothetical: it is the
  // purchase that closes the capital reconciliation, and the only way to record it was a
  // path that destroys more than it adds. (audit 2026-08-03)
  //
  // `avgCost` is maintained as the average over the WHOLE position by every writer
  // (lotAvgCost, saveAddLot, the monthly path), including the SAP case where migration v31
  // set it to investment-cost / total-qty — so qty * avgCost gives EUR 10,474 there too,
  // identical to the lot sum. One expression, correct for both shapes, and lots become
  // additive provenance rather than an all-or-nothing basis.
  if (h.avgCost && h.qty) {
    return _toUSD(h.qty * h.avgCost, h.currency);
  }
  if (h.lots && h.lots.length > 0) {
    const totalCost = h.lots.reduce((s, lot) => lot.type === 'drip' ? s : s + (lot.cost || lot.qty * (lot.price || 0)), 0);
    return _toUSD(totalCost, h.currency);
  }
  return getMs().reduce((s, m) => s + hMoInvUSD(h, m), 0);
}

/**
 * Total local currency invested across all months for a holding
 */
export function hTotLocal(h) {
  // Same order and same reason as hTotUSD — these two must never disagree about a holding.
  if (h.avgCost && h.qty) {
    return h.qty * h.avgCost;
  }
  if (h.lots && h.lots.length > 0) {
    return h.lots.reduce((s, lot) => lot.type === 'drip' ? s : s + (lot.cost || lot.qty * (lot.price || 0)), 0);
  }
  return getMs().reduce((s, m) => s + hMoLocal(h, m), 0);
}

/**
 * Holding value in local and USD
 * CE-08 Fix: For commodities with raw USD price, compute USD value directly
 * CE-06 Fix: Use direct EUR/USD rate when available
 */
export function hVal(h) {
  const p = _S.prices[h.ticker];
  if (!p || !h.qty || h.qty <= 0) return { vL: null, vU: null };

  // Cash-type holdings: qty IS the value in native currency (e.g. 362 USD = $362)
  // Price field stores FX rate at purchase time, NOT per-unit value
  if (h.type === 'Cash') {
    const vL = h.qty; // qty = amount in holding's currency
    let vU = vL;
    // FX INTEGRITY: without a usable rate a TRY/EUR amount is NOT a USD amount.
    // Returning it unconverted reported ₺1,900,000 as $1,900,000 in net worth.
    // null routes the holding into the marked "awaiting price" cost-basis path instead.
    if (h.currency === 'TRY') {
      vU = _S.fxRate > 0 ? vL / _S.fxRate : null;
    } else if (h.currency === 'EUR') {
      // fxEURUSD = EUR per 1 USD (e.g. 0.855) → divide to convert EUR → USD
      if (_S.fxEURUSD && _S.fxEURUSD > 0) vU = vL / _S.fxEURUSD;
      else if (_S.fxRateEUR > 0 && _S.fxRate > 0) vU = (vL * _S.fxRateEUR) / _S.fxRate;
      else vU = null;
    }
    // USD currency: vU = vL (already in USD)
    return { vL, vU };
  }

  const vL = h.qty * p.price;
  let vU = vL;
  // CE-08 Fix: For commodities with raw USD price, compute USD value directly (no FX round-trip)
  if (p._rawPriceUSD) {
    vU = (h.qty * p._rawPriceUSD) / 31.1035;
  } else if (h.currency === 'TRY' || p.cur === 'TRY') {
    // FX INTEGRITY: see the Cash branch above. No rate => no USD figure, ever.
    vU = _S.fxRate > 0 ? vL / _S.fxRate : null;
  }
  // CE-06 Fix: Use direct EUR/USD rate when available
  if (h.currency === 'EUR') {
    if (_S.fxEURUSD && _S.fxEURUSD > 0) vU = vL / _S.fxEURUSD;
    else if (_S.fxRateEUR > 0 && _S.fxRate > 0) vU = (vL * _S.fxRateEUR) / _S.fxRate;
    else vU = null;
  }
  return { vL, vU };
}

/**
 * A holding's contribution to net worth, in USD.
 *
 * Three callers inlined this rule and two of them got it wrong the same way:
 *
 *   const v = hVal(h); if (v.vU) total += v.vU; else total += hTotUSD(h);
 *
 * `hVal` returns null for two unrelated reasons — no price, or no position — and that `else`
 * treats both as "unpriced, fall back to cost". For a SOLD holding hTotUSD then walks past
 * qty*avgCost (qty is 0) and past the lots (a full exit leaves none) down to its last resort,
 * the monthly-plan history, and answers with what the holding cost over its whole life. Eight
 * IBKR positions exited on 2026-08-06 kept adding USD 5,167.93 to net worth on top of the cash
 * they had just been sold for, and the hero read USD 84,438 against a true USD 74,191.
 *
 * A closed position is worth nothing. An unpriced one is worth what was paid, and says so.
 * Those are different answers; the caller should not have to know that they ever collided.
 *
 * @returns {{usd:number, atCost:boolean}}
 */
export function hWealthUSD(h) {
  if (!h || !(h.qty > 0)) return { usd: 0, atCost: false };
  const v = hVal(h);
  if (v.vU) return { usd: v.vU, atCost: false };
  return { usd: hTotUSD(h) || 0, atCost: true };
}

/**
 * Holding profit/loss and percent
 * Includes sanity check: flags anomalies where P&L exceeds ±300% of invested
 */
export function hPnl(h) {
  const v = hVal(h);
  if (!v.vU) return { pnl: null, pnlP: null };
  const inv = hTotUSD(h);
  if (inv <= 0) return { pnl: null, pnlP: null };
  const pnl = v.vU - inv;
  const pnlP = (pnl / inv) * 100;
  // Sanity guard: catch phantom P&L from mismatched invested/value logic
  if (Math.abs(pnlP) > 300) {
    console.warn(`[ZAGX-SANITY] ${h.ticker}: P&L ${pnlP.toFixed(1)}% looks anomalous (inv=$${inv.toFixed(2)}, val=$${v.vU.toFixed(2)}, type=${h.type}, cur=${h.currency})`);
  }
  return { pnl, pnlP };
}

/**
 * FX-adjusted USD return estimate (read-only dashboard indicator).
 * Stored USD P&L converts each holding's cost at TODAY's FX, so it captures only
 * local-price moves and hides currency gain/loss on non-USD holdings. This restates
 * non-USD, non-cash cost basis at the average acquisition FX (mean of the tracked
 * monthlyRates) so the return reflects FX too. ESTIMATE: legacy holdings carry no
 * lot-level entry FX, so a period average is used. Cash holdings have no FX P&L here
 * (cost = current value, matching hTotUSD). Reuses hVal/hTotLocal — no cost model
 * change, nothing persisted.
 */
export function fxAdjReturn() {
  const mr = (_S && _S.monthlyRates) || {};
  // ACQUISITION-weighted, not calendar-weighted. The label says "cost at avg acq. FX", but
  // this took the unweighted mean of EVERY tracked month — including months with no
  // purchase at all — so a quiet month moved the stated acquisition rate as much as the
  // month the position was actually built. Weight each month by that month's investment
  // multiplier, which is the only purchase-size signal the monthly model carries.
  // (audit 2026-08-01)
  const entries = Object.entries(mr).filter(([, m]) => m && m.usdtry > 0);
  if (!entries.length) return null;
  const wOf = ([, m]) => (m.mul > 0 ? m.mul : 1);
  const wSum = entries.reduce((s, e) => s + wOf(e), 0);
  const avgUT = wSum > 0
    ? entries.reduce((s, e) => s + e[1].usdtry * wOf(e), 0) / wSum
    : entries.reduce((s, e) => s + e[1].usdtry, 0) / entries.length;
  const eurEntries = entries.filter(([, m]) => m.eurtry > 0);
  const eurWSum = eurEntries.reduce((s, e) => s + wOf(e), 0);
  const avgET = eurWSum > 0 ? eurEntries.reduce((s, e) => s + e[1].eurtry * wOf(e), 0) / eurWSum : 0;
  const avgEURUSD = avgET > 0 ? avgUT / avgET : null;
  let invAdj = 0, val = 0, adjusted = false;
  (_S.holdings || []).forEach(h => {
    const v = hVal(h);
    if (v.vU == null) return;
    val += v.vU;
    if (h.type === 'Cash') { invAdj += v.vU; return; }
    const lc = hTotLocal(h);
    if (h.currency === 'TRY') { invAdj += lc / avgUT; adjusted = true; }
    else if (h.currency === 'EUR' && avgEURUSD) { invAdj += lc / avgEURUSD; adjusted = true; }
    else invAdj += hTotUSD(h);
  });
  if (!adjusted || invAdj <= 0) return null;
  return { invAdj, val, pnl: val - invAdj, pct: (val - invAdj) / invAdj * 100, avgUT };
}

/**
 * Average cost per share owned, on the SAME basis the cost readers use.
 *
 * hTotUSD / hTotLocal exclude DRIP lots from cost because free shares are a return, not
 * capital. Both lot writers (portfolio.saveAddLot, dividends.saveDividend) used to average
 * over every lot including DRIP, so `h.avgCost` and the cost basis disagreed about the same
 * holding — live, SAP read €180.73 against a true €127.18, a €4,410 (~$5,074) overstatement
 * that hid an equal amount of gain. One definition, one place. (audit 2026-08-02)
 *
 * Denominator is TOTAL qty (DRIP shares are owned), numerator is INVESTMENT cost only.
 * Returns 0 for an empty or zero-quantity lot set rather than NaN.
 */
export function lotAvgCost(lots) {
  const ls = lots || [];
  const qty = ls.reduce((s, l) => s + ((l && l.qty) || 0), 0);
  if (!(qty > 0)) return 0;
  const cost = ls.reduce((s, l) => (l && l.type === 'drip') ? s : s + ((l && (l.cost || (l.qty || 0) * (l.price || 0))) || 0), 0);
  return cost / qty;
}

/**
 * Average cost after adding ONE lot to a position that may already have basis the lots do
 * not describe.
 *
 * Recomputing from the lot list alone is only right when the lots ARE the whole position.
 * Adding a dated purchase to a holding whose basis arrived as an average would otherwise
 * throw that average away — TRY 41,736 of ASELS deleted by recording a TRY 79,100 buy.
 * Blending incrementally is right in both cases: with no prior quantity it reduces to the
 * lot's own price, and with a prior position it does what a purchase actually does.
 *
 * A DRIP lot adds quantity and no cost, so the average falls — free shares diluting the
 * cost per share owned is exactly what should happen.
 */
export function blendedAvgCost(prevQty, prevAvgCost, lot) {
  const pq = +prevQty || 0;
  const pc = pq * (+prevAvgCost || 0);
  const lq = (lot && +lot.qty) || 0;
  const lc = (lot && lot.type === 'drip') ? 0 : ((lot && (lot.cost || lq * (+lot.price || 0))) || 0);
  const q = pq + lq;
  if (!(q > 0)) return 0;
  return (pc + lc) / q;
}

/**
 * The INVERSE of blendedAvgCost: the position after `exitQty` shares costing `exitCost` leave.
 *
 * Every writer that ADDS to a holding has blended incrementally since 2026-08-03, because a
 * holding can carry basis its lots do not describe. Every writer that REMOVED from one went on
 * recomputing `qty` and `avgCost` from the lot list — the exact operation that comment forbids,
 * pointing the other way. Two of them: `execExitH` and `deleteDividend`.
 *
 * Measured on live state 2026-08-14: SEVEN holdings carry a lot list shorter than the position,
 * created by the August rebalance adding + Lot purchases on top of averaged positions.
 * TEJ 25,665.74 units against 7,539 in lots. Selling the 7,539 recomputed `h.qty` to 0 and
 * deleted 18,126.74 units carrying TRY 37,410 of basis — and selling ONE share would have done
 * the same, because the recompute does not care how much was sold.
 *
 * A DRIP removal passes exitCost 0: the free shares that diluted the average are gone, so the
 * average rises. That is blendedAvgCost's DRIP branch run backwards.
 *
 * Returns the pair, so no caller can get one of the two right and the other wrong.
 */
export function holdingAfterExit(prevQty, prevAvgCost, exitQty, exitCost) {
  const pq = +prevQty || 0;
  const pc = pq * (+prevAvgCost || 0);
  const q = pq - (+exitQty || 0);
  // Below a share's dust the position is closed, not microscopically open.
  if (!(q > 1e-9)) return { qty: 0, avgCost: +prevAvgCost || 0 };
  return { qty: q, avgCost: Math.max(0, pc - (+exitCost || 0)) / q };
}

/**
 * The part of a position its lot list does not describe — quantity and cost.
 *
 * `qty * avgCost` is the whole basis, maintained by every writer. The lots describe some of it.
 * DRIP lots add quantity and no cost (blendedAvgCost), so the cost side subtracts `investedCost`
 * while the quantity side subtracts every lot. Zero for a holding whose lots ARE the position —
 * the SAP plan, and every position bought entirely through + Lot.
 */
export function averagedTranche(h) {
  const ls = lotSummary(h);
  const q = Math.max(0, (h && +h.qty || 0) - (ls ? ls.totalQty : 0));
  const c = Math.max(0, (h && +h.qty || 0) * (h && +h.avgCost || 0) - (ls ? ls.investedCost : 0));
  return { qty: q, cost: q > 0 ? c : 0, unit: q > 0 ? c / q : 0 };
}

/**
 * Lot-based holding summary — investment vs DRIP breakdown
 */
export function lotSummary(h) {
  if (!h.lots || !h.lots.length) return null;
  const inv = h.lots.filter(l => l.type === 'investment');
  const drip = h.lots.filter(l => l.type === 'drip');
  return {
    investedQty: inv.reduce((s, l) => s + l.qty, 0),
    investedCost: inv.reduce((s, l) => s + (l.cost || l.qty * l.price), 0),
    dripQty: drip.reduce((s, l) => s + l.qty, 0),
    dripCost: drip.reduce((s, l) => s + (l.cost || l.qty * l.price), 0),
    totalQty: h.lots.reduce((s, l) => s + l.qty, 0),
    totalCost: h.lots.reduce((s, l) => s + (l.cost || l.qty * l.price), 0),
    lotCount: h.lots.length
  };
}

/**
 * Time Deposit: compute gross return, net return after withholding
 * Supports monthly compound or daily compound
 */
/**
 * Compute daily TD value across rate segments.
 * Each segment: { startDate, rate, startBalance }
 * Returns { gross, totalDays, segments: [{ days, rate, startBal, endBal, interest }] }
 *
 * Backward-compatible: if no rateSegments, uses single td.annualRate + td.startDate.
 */
/**
 * THE PART OF A DEPOSIT THAT ACTUALLY EARNS.
 *
 * Every accrual in this file grew `td.principal`, which assumes the whole balance earns the
 * rate. Akbank's breakable TRY deposit does not work that way: a portion of the balance sits
 * inside the product and earns nothing.
 *
 * MEASURED 2026-09-11, and the owner's own figures are what caught it. He withdrew TRY
 * 1,212,800 and the bank left TRY 953,978.78, of which TRY 95,397.88 -- exactly 10% -- earns no
 * interest. So the interest actually credited was 2,166,778.78 - 2,143,167.77 = TRY 23,611.01,
 * while `tdAccrued` reported 26,289.72. **Overstated by TRY 2,678.71 on a fourteen-day hold,
 * 11.3%**, and not once: every reading of net worth, every accrued figure on the Money page and
 * every `closeDailyTd` would have carried it, growing with the balance.
 *
 * ⚠ THE FIRST NUMBER I PUT IN THIS DOCBLOCK WAS 27,446.19 AND IT WAS MINE, NOT THE APP'S. I had
 * replicated the accrual by hand as simple interest; `_dailyTdSegments` compounds daily and
 * floors the day count. Quoting a model's error from a reimplementation of the model is how a
 * fix gets built against a defect nobody has. The figures above come from `tdAccrued` itself.
 *
 * `principal` still means the whole balance, because that is what the bank statement says and
 * what a reconciliation compares against. `currentValue` is still `principal + netInt`. Only
 * the base the rate is applied to changes, and it is DECLARED rather than inferred -- a deposit
 * with no `nonEarning` earns on all of it, which is the ordinary case and what every other
 * deposit in this book does.
 */
export function tdEarningBase(td) {
  const p = Number(td && td.principal) || 0;
  const ne = Number(td && td.nonEarning) || 0;
  return Math.max(0, p - Math.max(0, ne));
}

function _dailyTdSegments(td, asOfMs) {
  // ⚠ THE END OF THE LAST SEGMENT IS A PARAMETER, NOT ALWAYS `now`.
  //
  // This read `Date.now()` unconditionally, so a daily deposit that had been CLOSED went on
  // earning interest forever. Measured 2026-08-15 on a TRYVD2 that opened 2026-04-08 and closed
  // 2026-04-30: it reported **₺142,382** of gross interest on 22 days at ~42 % — the honest
  // figure is ₺23,099. Nothing was wrong on his book only because every consumer filters
  // `status !== 'Closed'` before calling in; the day one forgets, it books ₺119,283 of interest
  // that was never earned. (ACCT-007)
  //
  // `tdAccrued` already accepted an `asOf` and silently dropped it on the daily branch — the
  // same defect wearing a parameter. Threading it through is what lets the tax summary ask
  // "what had accrued by 31 December" instead of "what has accrued by today". (MATH-001)
  const nowMs = asOfMs != null ? asOfMs : Date.now();
  const segs = td.rateSegments && td.rateSegments.length > 0
    ? td.rateSegments
    : [{ startDate: td.startDate, rate: td.annualRate, startBalance: tdEarningBase(td) }];

  let totalDays = 0;
  const result = [];

  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    const segStart = new Date(seg.startDate).getTime();
    // ⚠ EVERY segment is clamped to the cut-off, not just the last one.
    //
    // Before this, only the final segment ended at `nowMs`; the earlier ones always ran to the
    // next rate change. Ask this function about a date BEFORE the last rate change and it
    // answered with the full accrual up to that change — a deposit that started 2026-04-30
    // reported ₺24,445 of interest "as of 2026-01-01", four months before it existed.
    //
    // Harmless while the only question was "as of now", which is later than every segment. The
    // moment the tax summary began asking "as of 31 December" it would have booked a following
    // year's rate period into this year's return.
    if (segStart >= nowMs) break;
    const rawEnd = (i < segs.length - 1) ? new Date(segs[i + 1].startDate).getTime() : nowMs;
    const segEnd = Math.min(rawEnd, nowMs);
    const days = Math.max(0, Math.floor((segEnd - segStart) / 86400000));
    const endBal = seg.startBalance * Math.pow(1 + seg.rate / 100 / 365, days);
    const interest = endBal - seg.startBalance;
    result.push({ days, rate: seg.rate, startBal: seg.startBalance, endBal, interest });
    totalDays += days;
  }

  const gross = result.length > 0 ? result[result.length - 1].endBal : tdEarningBase(td);
  return { gross, totalDays, segments: result };
}

export function tdComputeGross(td, asOf) {
  if (!td || !td.principal) return null;
  const kind = td.kind || 'monthly';
  const _base = tdEarningBase(td);
  let gross = null,
    rate = null;
  if (kind === 'monthly') {
    if (!td.termMonths) return null;
    // `base` and not `principal`: see tdEarningBase. A monthly deposit with no `nonEarning`
    // reduces to the whole balance, so nothing changes for the ordinary case.
    if (td.mode === 'maturity' && td.maturityValue) {
      gross = td.maturityValue;
      rate = (Math.pow(gross / _base, 12 / td.termMonths) - 1) * 100;
    } else if (td.annualRate != null) {
      rate = td.annualRate;
      gross = _base * Math.pow(1 + rate / 100 / 12, td.termMonths);
    }
  } else {
    if (td.annualRate == null || !td.startDate) return null;
    // Use segment-aware calculation
    const seg = _dailyTdSegments(td, asOf != null ? new Date(asOf).getTime() : null);
    gross = seg.gross;
    // Current active rate (last segment)
    rate = seg.segments.length > 0 ? seg.segments[seg.segments.length - 1].rate : td.annualRate;
  }
  if (gross == null) return null;
  // The interest is what the EARNING base grew by. Subtracting the whole principal here was the
  // second half of the same defect: it would have reported a LOSS on a deposit whose
  // non-earning portion is large. (audit 2026-09-11)
  const grossInt = gross - _base;
  const wht = (td.withholdingPct || 0) / 100;
  const netInt = grossInt * (1 - wht);
  // dailyGross based on CURRENT rate and CURRENT balance (last segment)
  const currentRate = rate || td.annualRate || 0;
  const currentBal = kind === 'daily' ? (gross) : td.principal;
  const dailyGross =
    kind === 'daily' ? (currentBal * (currentRate / 100)) / 365 : null;
  const dailyNet = dailyGross != null ? dailyGross * (1 - wht) : null;
  return {
    gross,
    grossInt,
    netInt,
    netMaturity: td.principal + netInt,
    rate,
    kind,
    dailyGross,
    dailyNet,
  };
}

/**
 * Time Deposit: compute accrued interest as of a date
 */
export function tdAccrued(td, asOf) {
  const c = tdComputeGross(td, asOf);
  if (!c || !td.startDate) return null;
  const start = new Date(td.startDate).getTime();
  const kind = td.kind || 'monthly';
  if (kind === 'daily') {
    const seg = _dailyTdSegments(td, asOf != null ? new Date(asOf).getTime() : null);
    return {
      ...c,
      maturityDate: null,
      daysOpen: seg.totalDays,
      accruedNet: c.netInt,
      currentValue: td.principal + c.netInt,
      daysToMat: null,
      matured: false,
      dailyGross: c.dailyGross,
      dailyNet: c.dailyNet,
      segments: seg.segments,
      rateChanges: (td.rateSegments || []).length - 1,
    };
  }
  // F-09 Fix: stepwise compound for monthly TDs instead of linear interpolation
  // Completed months accrue full compound interest, partial current month is linear
  const totalMs = td.termMonths * 30.4375 * 86400000;
  const matDate = start + totalMs;
  const nowMs = asOf ? new Date(asOf).getTime() : Date.now();
  const elapsed = Math.max(0, Math.min(nowMs - start, totalMs));
  const monthsElapsed = elapsed / (30.4375 * 86400000);
  const fullMonths = Math.floor(monthsElapsed);
  const partialFrac = monthsElapsed - fullMonths;
  const rate = c.rate || td.annualRate || 0;
  const wht = (td.withholdingPct || 0) / 100;
  // Gross interest for completed months (compound)
  const grossAfterFull = td.principal * Math.pow(1 + rate / 100 / 12, fullMonths) - td.principal;
  // Partial month: linear interpolation of next month's interest
  const nextMonthGross = td.principal * Math.pow(1 + rate / 100 / 12, fullMonths) * (rate / 100 / 12);
  const grossPartial = nextMonthGross * partialFrac;
  const totalGrossAccrued = grossAfterFull + grossPartial;
  const accruedNet = totalGrossAccrued * (1 - wht);
  const frac = td.termMonths > 0 ? monthsElapsed / td.termMonths : 0;
  const daysToMat = Math.ceil((matDate - Date.now()) / 86400000);
  return {
    ...c,
    maturityDate: new Date(matDate).toISOString(),
    accruedNet,
    currentValue: td.principal + accruedNet,
    daysToMat,
    frac,
    matured: Date.now() >= matDate,
    // Monthly run-rate, mirroring dailyGross/dailyNet on the daily branch.
    // tdComputeGross sets dailyGross ONLY for kind === 'daily', and this branch used to
    // expose no monthly equivalent and no daysOpen either — so every consumer estimating
    // a monthly income figure fell through both of its branches and credited a fixed-term
    // deposit with exactly 0/month while showing its accrued interest elsewhere.
    // (audit 2026-08-01). nextMonthGross is next month's compound interest on the
    // current balance, i.e. the forward run-rate, not a backward average.
    monthlyGross: nextMonthGross,
    monthlyNet: nextMonthGross * (1 - wht),
    daysOpen: Math.max(0, Math.round(elapsed / 86400000)),
  };
}

/**
 * Calculate hypothetical benchmark (100% VOO DCA) value
 */
/**
 * Hypothetical "what if I had just bought VOO on the same schedule".
 *
 * A month is only scored when a REAL benchmark price for that month exists. The previous
 * version fell back to `vooNow` for any month without a snapshot:
 *
 *     const vooAtMonth = snapForMonth?.benchmarks?.VOO || vooNow;
 *
 * Buying at today's price and valuing at today's price yields exactly 0% for that month.
 * S.snapshots is capped at 365 days and is only written on days the dashboard rendered,
 * so most historical months took that branch and the benchmark was structurally dragged
 * toward zero — making `alpha = yours - benchmark` fabricated outperformance. That is a
 * look-ahead: an evaluation-time price used where an event-time price is required.
 * (audit 2026-08-01)
 *
 * Unpriced months are now EXCLUDED from both legs, and the coverage is returned so the
 * caller can say how much of the history is actually comparable instead of implying all
 * of it is.
 *
 * @returns {null|{value, shares, invested, monthsScored, monthsTotal, coverage, skipped[]}}
 */
export function calcBenchmarkDCA() {
  const vooNow = _S.prices['VOO']?.price;
  if (!vooNow) return null;
  const ms = getMs();
  if (!ms.length) return null;

  let totalShares = 0;
  let totalInvested = 0;
  let monthsTotal = 0;
  const usedPlanned = false;
  const skipped = [];
  const skippedNoFlow = [];
  const extSeries = netExternalByMonth(_S, _toUSDAt);

  ms.forEach(m => {
    const r = _S.monthlyRates[m];
    if (!r) return;
    monthsTotal++;
    // Backfilled monthly close first, then that month's snapshot. Never a current price.
    const stored = _S.benchmarkPrices && _S.benchmarkPrices.VOO && _S.benchmarkPrices.VOO[m];
    const snapForMonth = (_S.snapshots || []).find(s => s && typeof s.date === 'string' && s.date.startsWith(m) && s.benchmarks && s.benchmarks.VOO > 0);
    const vooAtMonth = stored > 0 ? stored : (snapForMonth && snapForMonth.benchmarks.VOO);
    if (!(vooAtMonth > 0)) { skipped.push(m); return; }
    // NEW MONEY, from the cash ledger. Not the plan (S.base is $2,400 against roughly
    // $15,000 actually deployed) and not what was bought (a rebalance is a purchase, so
    // gross purchases totalled $51,394 against a $42,291 cost basis). A DCA benchmark
    // models capital ENTERING the system, and only the ledger records that.
    // (audit 2026-08-01)
    const amtUSD = externalForMonth(extSeries, m);
    if (amtUSD == null) {
      // No trustworthy ledger record for this month — skip it, exactly as an unpriced
      // month is skipped. Substituting anything here fabricates a contribution.
      skippedNoFlow.push(m);
      return;
    }
    if (!(amtUSD > 0)) return;   // a flat or net-outflow month buys nothing
    totalInvested += amtUSD;
    totalShares += amtUSD / vooAtMonth;
  });

  const monthsScored = monthsTotal - skipped.length;
  // Cost basis the benchmark could NOT model: holdings carrying a direct avgCost with no
  // month attribution (the same set the 2026-07-31 XIRR audit found). Surfaced so the card
  // can state how much of the book the comparison actually covers.
  const portfolioCost = (_S.holdings || []).reduce((s, h) => {
    if (h && h.type !== 'Cash') return s + (hTotUSD(h) || 0);
    return s;
  }, 0);
  // The schedule is now net external capital, so it SHOULD exceed the holdings cost
  // basis: money legitimately lands in cash, the reserve fund, time deposits and open
  // trades, none of which are in portfolioCost. Comparing the two was the correct guard
  // while the basis was gross purchases; against this basis it is a false alarm. What the
  // comparison needs instead is a portfolio side measured on the SAME capital — which is
  // exactly netGrowthUSD (total wealth vs net external capital), not holdings P&L.
  // (audit 2026-08-01)
  return {
    value: totalShares * vooNow,
    shares: totalShares,
    invested: totalInvested,
    monthsScored, monthsTotal,
    coverage: monthsTotal > 0 ? monthsScored / monthsTotal : 0,
    skipped,
    usedPlanned,
    portfolioCost,
    costCoverage: portfolioCost > 0 ? totalInvested / portfolioCost : null,
    basis: 'net-external-capital',
    skippedNoFlow,
    externalTotal: extSeries.total,
  };
}

/**
 * Benchmark timeline: compute hypothetical VOO value at each snapshot
 */
export function benchmarkTimeline(snaps) {
  if (!snaps.length) return null;
  const ms = getMs();
  if (!ms.length) return null;
  // Build month→deposit map
  const deposits = [];
  ms.forEach(m => {
    const r = _S.monthlyRates[m];
    if (!r) return;
    deposits.push({ month: m, amt: _S.base * (r.mul || 1) });
  });
  if (!deposits.length) return null;
  // For each snapshot, compute cumulative benchmark value
  const vooNow = _S.prices['VOO']?.price;
  if (!vooNow) return null;
  let accShares = 0;
  let lastVOOPrice = null;
  const depsCopy = [...deposits]; // Clone to prevent mutation across re-renders
  // Price resolution order, matching calcBenchmarkDCA: backfilled monthly close, then
  // that snapshot's own benchmark, then the last KNOWN price. The `|| vooNow` tail that
  // used to close this chain is the look-ahead removed from the DCA card today — buying
  // at today's price and valuing at today's price contributes exactly 0% for the month
  // and flattens the charted benchmark toward the portfolio line. A month with no price
  // from any source now buys nothing rather than buying at a fabricated one, and the
  // backfilled closes (S.benchmarkPrices) are consulted here too. (audit 2026-08-01)
  const storedFor = m => {
    const v = _S.benchmarkPrices && _S.benchmarkPrices.VOO && _S.benchmarkPrices.VOO[m];
    return v > 0 ? v : null;
  };
  const values = snaps.map(snap => {
    const snapMonth = String(snap.date || '').slice(0, 7);
    // Add shares for any deposits up to this snapshot's month
    while (depsCopy.length && depsCopy[0].month <= snapMonth) {
      const dep = depsCopy.shift();
      const vooPrice = storedFor(dep.month) || snap.benchmarks?.VOO || lastVOOPrice;
      if (vooPrice > 0) accShares += dep.amt / vooPrice;
    }
    const voo = storedFor(snapMonth) || snap.benchmarks?.VOO || lastVOOPrice || vooNow;
    if (snap.benchmarks?.VOO) lastVOOPrice = snap.benchmarks.VOO;
    return accShares * voo;
  });
  return values;
}

/**
 * Re-export toUSD from utils for backward compatibility
 */
export const toUSD = _toUSD;

/**
 * Re-export normTicker, for the same reason toUSD is re-exported here.
 *
 * ⚠ THIS IS A BOUNDARY, NOT A CONVENIENCE. `risk-calc.js` may import `./state.js` and `./math.js`
 * and nothing else — the rule is asserted in `two-answers-to-how-much-do-i-have.test.js`, and it
 * is what keeps that file importable from every page without closing a cycle. Exposure needs the
 * one normaliser (registry row `ticker.normalised`, six copies deleted to get there), so it comes
 * through the same door `toUSD` already uses instead of the boundary being widened for it.
 * (2026-08-16)
 */
export const normTicker = _normTicker;
